import React from "react";
import {
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { C } from "../theme";
import Button from "../components/Button";
import Card from "../components/Card";
import { db } from "../firebase";
import { getCurrentSessionAttribution } from "../utils/promo";
import {
  getSortedCountries,
  detectCountryFromLocale,
  isValidCountry,
} from "../data/countries";
import {
  AsYouType,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "libphonenumber-js";
import CountryFlag from "../components/CountryFlag";
import {
  useAnglerCatalog,
  searchSpecies,
  searchLures,
  findById,
} from "../data/anglerCatalog";
import { WATER_TYPE_OPTIONS, SUBTYPE_OPTIONS } from "../data/waterTypes";
import QrScanner from "../components/QrScanner";
import { redeemBookToken, extractBookToken } from "../services/bookRedeem";

const EULA_VERSION = "2026-04-16";

// Format a phone number as the user types using the selected country's national
// convention (libphonenumber AsYouType). With no country chosen yet it just
// lightly sanitizes the input.
function formatNationalPhone(value, countryCode) {
  const raw = String(value || "");
  if (!isValidCountry(countryCode)) return raw.replace(/[^\d\s\-()+]/g, "");
  try {
    return new AsYouType(countryCode).input(raw);
  } catch {
    return raw;
  }
}

// International dialing code for a country (e.g. "US" -> "1", "MX" -> "52").
function safeCallingCode(countryCode) {
  if (!isValidCountry(countryCode)) return "";
  try {
    return getCountryCallingCode(countryCode);
  } catch {
    return "";
  }
}

const ROLE_OPTIONS = [
  {
    id: "fisherman",
    label: "Angler",
    help: "Records catches and uploads videos.",
  },
  {
    id: "director",
    label: "Tournament Director",
    help: "Monitor all teams, standings, and videos (view-only).",
    beta: true,
  },
  {
    id: "participant",
    label: "Tournament Spectator",
    help: "Follow teams and watch tournament videos (view-only).",
    beta: true,
  },
  {
    id: "sponsor",
    label: "Sponsor",
    help: "Brand/partner view of tournament standings and videos (view-only).",
    beta: true,
  },
];

const ROLE_CONFIRM = {
  fisherman: {
    title: "Confirm Angler role",
    body:
      "Angler is the role for people who fish. You'll log your catches, record and upload certified catch videos, and build your personal history and stats over time.\n\n" +
      "When you fish a tournament, this role lets you compete — your certified catches count toward the live leaderboard and standings.\n\n" +
      "Choosing this sets your account to the Angler experience. If instead you only want to run or watch a tournament without fishing it, choose Tournament Director, Tournament Spectator, or Sponsor.",
    confirmLabel: "Yes, I'm an Angler",
  },
  director: {
    title: "Confirm Tournament Director role",
    body:
      "Tournament Director gives you a live, big-picture view of the tournaments you run. For every event you've created, you'll follow all teams on the leaderboard, watch the certified catch videos as they come in, and track standings in real time.\n\n" +
      "You create and manage your tournaments in the99 dashboard app \u2014 setting the species, boundaries, and dates there. This role only shows the events you've established, not other directors' tournaments.\n\n" +
      "This is a view-only role \u2014 it does not log catches, record video, or fish a tournament. It's built for running and monitoring an event, not competing in it.\n\n" +
      "Choosing this sets your account to the Tournament Director experience. If you also plan to fish and log your own catches, choose Angler instead.",
    confirmLabel: "Yes, I'm a Director",
  },
  participant: {
    title: "Confirm Tournament Spectator role",
    body:
      "Tournament Spectator lets you follow the action without competing. You'll see the live leaderboard, keep tabs on your favorite teams and anglers, and watch their certified catch videos as the tournament unfolds.\n\n" +
      "This is a view-only role \u2014 it does not log catches or upload video. It's for fans and supporters who want a front-row seat.\n\n" +
      "Choosing this sets your account to the Spectator experience. If you intend to fish and record your own catches, choose Angler instead.",
    confirmLabel: "Yes, I'm a Spectator",
  },
  sponsor: {
    title: "Confirm Sponsor role",
    body:
      "Sponsor is for brands and partners backing a tournament. You'll have a leaderboard-side view of the event \u2014 following standings, teams, and certified catch videos as they happen \u2014 so you can see your tournament come to life.\n\n" +
      "This is a view-only role \u2014 it does not log catches or upload video.\n\n" +
      "Choosing this sets your account to the Sponsor experience. If you also plan to fish and log catches, choose Angler instead.",
    confirmLabel: "Yes, I'm a Sponsor",
  },
};

const splitDisplayName = (displayName = "") => {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

export default function EnrollmentScreen({ user, profile, onComplete, onCancel }) {
  const derivedName = React.useMemo(() => splitDisplayName(user?.displayName || ""), [user?.displayName]);

  const [role, setRole] = React.useState(profile?.role || "fisherman");
  const [firstName, setFirstName] = React.useState(profile?.firstName || derivedName.firstName || "");
  const [lastName, setLastName] = React.useState(profile?.lastName || derivedName.lastName || "");
  const [phone, setPhone] = React.useState(profile?.phone || "");
  // Country of origin — drives the header flag and tells us which regions have
  // enough anglers to justify localizing the app for them. Pre-fills from the
  // device locale as a low-friction default the user can change.
  const [country, setCountry] = React.useState(
    profile?.country || detectCountryFromLocale() || ""
  );
  const countryOptions = React.useMemo(() => getSortedCountries(), []);
  // Anglers can fish saltwater, freshwater, or both. `waterTypes` is the
  // source of truth (array); the legacy single `waterType` string is kept in
  // sync on save for back-compat with older readers.
  const [waterTypes, setWaterTypes] = React.useState(() =>
    Array.isArray(profile?.waterTypes) && profile.waterTypes.length > 0
      ? profile.waterTypes
      : profile?.waterType
      ? [profile.waterType]
      : []
  );
  const [subTypes, setSubTypes] = React.useState(
    Array.isArray(profile?.subTypes)
      ? profile.subTypes
      : profile?.subType
      ? [profile.subType]
      : []
  );
  const [showRoleInfo, setShowRoleInfo] = React.useState(false);
  // View-only role (director/participant/sponsor) awaiting confirmation. Null
  // when no confirmation dialog is open.
  const [pendingRole, setPendingRole] = React.useState(null);
  const [eulaAccepted, setEulaAccepted] = React.useState(Boolean(profile?.eula?.accepted));
  const [trialSpecies, setTrialSpecies] = React.useState(profile?.trialTrack?.species || "");
  const [trialLure, setTrialLure] = React.useState(profile?.trialTrack?.lure || "");
  const [trialLiveBait, setTrialLiveBait] = React.useState(profile?.trialTrack?.liveBait || "");
  const [speciesQuery, setSpeciesQuery] = React.useState("");
  const [lureQuery, setLureQuery] = React.useState("");
  const [liveBaitQuery, setLiveBaitQuery] = React.useState("");
  const [showSpeciesList, setShowSpeciesList] = React.useState(false);
  const [showLureList, setShowLureList] = React.useState(false);
  const [showLiveBaitList, setShowLiveBaitList] = React.useState(false);
  // Opt-in (default off): share catch videos to the dashboard + YouTube.
  // Anglers can flip this anytime from the dashboard's User Preferences.
  const [shareVideos, setShareVideos] = React.useState(Boolean(profile?.shareVideos));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // Book-buyer offer (Anglers only): scan the QR inside Before the Cast to
  // unlock the reader reward. The scan is validated server-side (signature +
  // one-per-account + global cap) which writes users/{uid}.bookDiscount; the
  // dashboard applies the reward to the reader's account.
  const alreadyRedeemed = Boolean(profile?.bookDiscount?.status === "eligible");
  const [showBookScanner, setShowBookScanner] = React.useState(false);
  const [bookScanState, setBookScanState] = React.useState(alreadyRedeemed ? "verified" : "idle");
  const [bookScanMessage, setBookScanMessage] = React.useState("");
  const bookVerified = bookScanState === "verified";

  const handleBookScanResult = React.useCallback(async (scanned) => {
    setShowBookScanner(false);
    const token = extractBookToken(scanned);
    if (!token) {
      setBookScanState("error");
      setBookScanMessage("That doesn't look like a Before the Cast book QR. Please scan the code printed inside the book.");
      return;
    }
    setBookScanState("verifying");
    setBookScanMessage("");
    try {
      await redeemBookToken(token, { attested: true });
      setBookScanState("verified");
      setBookScanMessage("Verified — your 6-month reader reward is unlocked.");
    } catch (err) {
      setBookScanState("error");
      if (err?.code === "cap_reached") {
        setBookScanMessage("This reader offer has reached its redemption limit.");
      } else if (err?.code === "invalid_token") {
        setBookScanMessage("That book code isn't valid. Please scan the QR printed inside your book.");
      } else {
        setBookScanMessage("Couldn't verify the code right now. You can finish enrolling and try again later.");
      }
    }
  }, []);

  // Activation writes firstName/lastName/phone/eula to the user doc
  // asynchronously, so this screen often mounts before that snapshot arrives.
  // Backfill empty fields once the profile lands; never clobber edits in flight.
  React.useEffect(() => {
    if (!profile) return;
    if (!firstName && profile.firstName) setFirstName(profile.firstName);
    if (!lastName && profile.lastName) setLastName(profile.lastName);
    if (!phone && profile.phone) setPhone(formatNationalPhone(profile.phone, country || profile.country));
    if (!country && profile.country) setCountry(profile.country);
    if (!eulaAccepted && profile.eula?.accepted) setEulaAccepted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.firstName, profile?.lastName, profile?.phone, profile?.eula?.accepted]);

  // The EULA is accepted earlier in the signup flow (the Welcome screen for
  // email/password, or the OAuth disclaimer). When that acceptance already
  // landed on the profile, don't re-present a toggleable checkbox here — the
  // user shouldn't be able to un-accept it at enrollment.
  const eulaPreviouslyAccepted = Boolean(profile?.eula?.accepted);

  // Selecting a country re-masks any digits already typed under that country's
  // national format (and the +code prefix beside the field updates with it).
  const handleCountryChange = (code) => {
    setCountry(code);
    setPhone((prev) => formatNationalPhone(prev, code));
  };

  // Every role now confirms via a modal that explains it before it's applied.
  // Angler explains the competing/logging experience; the view-only roles
  // explain that they lock the account into a leaderboard-only experience.
  const handleRoleSelect = (roleId) => {
    if (ROLE_CONFIRM[roleId]) {
      setPendingRole(roleId);
    } else {
      setRole(roleId);
    }
  };

  const confirmPendingRole = () => {
    if (pendingRole) setRole(pendingRole);
    setPendingRole(null);
  };

  const cancelPendingRole = () => setPendingRole(null);

  const submitEnrollment = async (e) => {
    e.preventDefault();
    setError("");

    const first = firstName.trim();
    const last = lastName.trim();
    const phoneDigits = phone.replace(/\D/g, "");
    let e164Phone = "";
    const isHarness = process.env.REACT_APP_USE_EMULATORS === "true";
    const resolvedTrialSpecies = String(trialSpecies || "").trim() || (isHarness ? "redfish" : "");
    // Fallbacks must be real catalog doc IDs (see anglerCatalogSeed.js); the
    // gold spoon lure is seeded as "spoon_gold", not "gold_spoon".
    const resolvedTrialLure = String(trialLure || "").trim() || (isHarness ? "spoon_gold" : "");
    const resolvedTrialLiveBait = String(trialLiveBait || "").trim();

    if (!first || !last) {
      setError("First and last name are required.");
      return;
    }
    if (!isValidCountry(country)) {
      setError("Please select your country.");
      return;
    }
    if (!phoneDigits) {
      setError("Mobile phone number is required.");
      return;
    }
    try {
      if (isValidPhoneNumber(phone, country)) {
        e164Phone = parsePhoneNumber(phone, country).number;
      }
    } catch {
      e164Phone = "";
    }
    if (!e164Phone) {
      setError("Enter a valid mobile number for the selected country.");
      return;
    }
    if (role === "fisherman") {
      if (waterTypes.length === 0) {
        setError("Please select Saltwater, Freshwater, or both.");
        return;
      }
      for (const wt of waterTypes) {
        const validSubtypeIds = (SUBTYPE_OPTIONS[wt] || []).map((opt) => opt.id);
        const chosen = subTypes.filter((id) => validSubtypeIds.includes(id));
        if (chosen.length === 0) {
          setError(
            wt === "saltwater"
              ? "Please pick Inshore, Offshore, or both."
              : "Please pick River, Lake, or both."
          );
          return;
        }
      }
      if (!resolvedTrialSpecies) {
        setError("Please pick the fish species you want to track.");
        return;
      }
      const hasLure = Boolean(resolvedTrialLure);
      const hasLiveBait = Boolean(resolvedTrialLiveBait);
      if (!hasLure && !hasLiveBait) {
        setError("Please pick a lure or a live bait to track.");
        return;
      }
    }
    if (!eulaAccepted) {
      setError("You must accept the EULA before completing enrollment.");
      return;
    }

    setLoading(true);
    try {
      const userPayload = {
        uid: user.uid,
        email: (user.email || profile?.email || "").trim(),
        phone: e164Phone || phoneDigits,
        country: String(country || "").toUpperCase(),
        firstName: first,
        lastName: last,
        role,
        inTournament: false,
        tournamentId: "",
        tournamentName: "",
        teamId: "",
        teamName: "",
        followingTeamIds: [],
        updatedAt: serverTimestamp(),
      };

      if (role === "fisherman") {
        const validSubtypeIds = waterTypes.flatMap((wt) =>
          (SUBTYPE_OPTIONS[wt] || []).map((opt) => opt.id)
        );
        const cleanedSubTypes = subTypes.filter((id) => validSubtypeIds.includes(id));
        userPayload.waterTypes = waterTypes;
        // Back-compat: keep the legacy single-value field populated. When the
        // angler fishes both, store "both" so freshwater-only readers don't
        // mistakenly treat them as freshwater (tides stay available).
        userPayload.waterType = waterTypes.length === 1 ? waterTypes[0] : "both";
        userPayload.subTypes = cleanedSubTypes;
        // Back-compat: keep legacy single-value field populated with the
        // first selection so any older readers don't break.
        userPayload.subType = cleanedSubTypes[0] || "";
        // Trial focus: required species + at least one of lure / live bait.
        // In harness builds we tolerate flaky mobile input events by falling
        // back to seeded defaults when fields are left blank.
        userPayload.trialTrack = {
          species: resolvedTrialSpecies,
          lure: resolvedTrialLure,
          liveBait: resolvedTrialLiveBait,
        };
      }

      if (!profile?.enrolledAt) {
        userPayload.enrolledAt = serverTimestamp();
      }
      if (!profile?.eula?.accepted) {
        userPayload.eula = {
          accepted: true,
          version: EULA_VERSION,
          acceptedAt: serverTimestamp(),
          acceptedVia: "enrollment",
        };
      }

      // First-touch promo attribution: only write if not already set on the
      // profile, so re-enrollment doesn't overwrite the original source.
      if (!profile?.attribution?.source) {
        const attribution = getCurrentSessionAttribution();
        if (attribution?.source) {
          userPayload.attribution = {
            source: attribution.source,
            capturedAt: attribution.capturedAt || Date.now(),
            recordedAt: serverTimestamp(),
          };
        } else if (bookVerified) {
          // Scanned + validated the book QR during enrollment — mark the
          // analytics attribution too (the authoritative entitlement is the
          // server-written users/{uid}.bookDiscount).
          userPayload.attribution = {
            source: "book",
            capturedAt: Date.now(),
            recordedAt: serverTimestamp(),
          };
        }
      }

      await setDoc(doc(db, "users", user.uid), userPayload, { merge: true });

      // Mirror the trial picks into userPreferences/{uid} so the dashboard's
      // My Fish Species / My Lures / My Live Bait pages render with the
      // user's enrollment selections already chosen. Fields are merged, so
      // empty selections don't clobber existing arrays.
      if (role === "fisherman") {
        const prefsPayload = { updatedAt: serverTimestamp() };
        const trimmedSpecies = trialSpecies ? String(trialSpecies).trim() : "";
        const trimmedLure = trialLure ? String(trialLure).trim() : "";
        const trimmedLiveBait = trialLiveBait ? String(trialLiveBait).trim() : "";
        if (trimmedSpecies) prefsPayload.fishSpeciesIds = [trimmedSpecies];
        if (trimmedLure) prefsPayload.lureIds = [trimmedLure];
        if (trimmedLiveBait) prefsPayload.liveBaitIds = [trimmedLiveBait];
        prefsPayload.shareVideos = shareVideos;
        if (Object.keys(prefsPayload).length > 1) {
          try {
            await setDoc(doc(db, "userPreferences", user.uid), prefsPayload, { merge: true });
          } catch (prefsErr) {
            // Non-fatal: enrollment succeeded; dashboard pages just won't
            // pre-populate. Don't block onComplete on this.
            console.warn("[Enrollment] userPreferences seed failed:", prefsErr);
          }
        }
      }

      onComplete?.();
    } catch (err) {
      setError(err?.message || "Could not complete enrollment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hide-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "16px 14px 90px" }}>
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3, marginBottom: 4 }}>THE 99</div>
        <div style={{ fontSize: 20, color: C.white, fontFamily: "Georgia,serif", fontWeight: 700 }}>
          Enrollment Setup
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>
        Choose your role and contact details.
      </div>

      {showBookScanner && (
        <QrScanner
          title="Scan your book QR"
          hint="Point your camera at the QR code printed inside Before the Cast."
          onResult={handleBookScanResult}
          onClose={() => { setShowBookScanner(false); if (bookScanState === "scanning") setBookScanState("idle"); }}
        />
      )}

      <Card>
        <form onSubmit={submitEnrollment} style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              background: C.cardBg,
              padding: 10,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 11, color: C.white, fontWeight: 700 }}>End User License Agreement</div>
            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45 }}>
              Plain-language summary: THE 99 helps you log catches, media, and tournament activity. You are responsible for legal compliance, safe use, and account security. AI insights are informational only.
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a href="/eula.html" target="_blank" rel="noreferrer" style={{ fontSize: 10, color: C.seafoam, textDecoration: "none" }}>View EULA</a>
              <a href="/privacy-policy.html" target="_blank" rel="noreferrer" style={{ fontSize: 10, color: C.seafoam, textDecoration: "none" }}>Privacy Policy</a>
              <a href="/data-deletion-instructions.html" target="_blank" rel="noreferrer" style={{ fontSize: 10, color: C.seafoam, textDecoration: "none" }}>Data Deletion</a>
            </div>
            {eulaPreviouslyAccepted ? (
              <div
                data-testid="enroll-eula-accepted"
                style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 10, color: C.seafoam }}
              >
                <span style={{ marginTop: 1 }}>✓</span>
                <span>You accepted the EULA (Version {EULA_VERSION}) during sign-up.</span>
              </div>
            ) : (
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 10, color: C.white, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  id="enroll-eula"
                  data-testid="enroll-eula"
                  checked={eulaAccepted}
                  onChange={(e) => setEulaAccepted(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <span>I agree to the EULA (Version {EULA_VERSION}) before setting up my profile.</span>
              </label>
            )}
          </div>

          <div
            style={{
              opacity: eulaAccepted ? 1 : 0.45,
              pointerEvents: eulaAccepted ? "auto" : "none",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
          <div style={{ display: "flex", gap: 10 }}>
            <input
              type="text"
              id="enroll-firstname"
              data-testid="enroll-firstname"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              style={{
                width: "50%",
                boxSizing: "border-box",
                padding: "11px 12px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.cardBg,
                color: C.white,
                outline: "none",
              }}
            />
            <input
              type="text"
              id="enroll-lastname"
              data-testid="enroll-lastname"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              style={{
                width: "50%",
                boxSizing: "border-box",
                padding: "11px 12px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.cardBg,
                color: C.white,
                outline: "none",
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              id="enroll-country"
              data-testid="enroll-country"
              value={country}
              onChange={(e) => handleCountryChange(e.target.value)}
              aria-label="Country"
              style={{
                flex: 1,
                boxSizing: "border-box",
                padding: "11px 12px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.cardBg,
                color: country ? C.white : C.muted,
                outline: "none",
                colorScheme: "dark",
              }}
            >
              <option value="" disabled>
                Select your country…
              </option>
              {countryOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            {isValidCountry(country) ? (
              <CountryFlag code={country} size={24} />
            ) : null}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 0,
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: C.cardBg,
              overflow: "hidden",
            }}
          >
            {safeCallingCode(country) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "11px 10px",
                  borderRight: `1px solid ${C.border}`,
                  color: C.white,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                <CountryFlag code={country} size={18} />
                {`+${safeCallingCode(country)}`}
              </div>
            ) : null}
            <input
              type="tel"
              id="enroll-phone"
              data-testid="enroll-phone"
              placeholder={country ? "Mobile number" : "Select your country first"}
              value={phone}
              onChange={(e) => setPhone(formatNationalPhone(e.target.value, country))}
              disabled={!isValidCountry(country)}
              autoComplete="tel"
              inputMode="tel"
              style={{
                flex: 1,
                minWidth: 0,
                boxSizing: "border-box",
                padding: "11px 12px",
                border: "none",
                background: "transparent",
                color: C.white,
                outline: "none",
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
            <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>ROLE</div>
            <button
              type="button"
              onClick={() => setShowRoleInfo((value) => !value)}
              aria-label="Explain roles"
              style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                border: `1px solid ${C.border}`,
                background: "transparent",
                color: C.muted,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                padding: 0,
              }}
            >
              i
            </button>
          </div>
          {showRoleInfo && (
            <div
              style={{
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                padding: 10,
                background: C.cardBg,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {ROLE_OPTIONS.map((option) => (
                <div key={option.id} style={{ fontSize: 10, color: C.muted, lineHeight: 1.5 }}>
                  <span style={{ color: C.white, fontWeight: 700 }}>{option.label}:</span> {option.help}
                </div>
              ))}
            </div>
          )}
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, background: C.cardBg }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {ROLE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  id={`enroll-role-${option.id}`}
                  data-testid={`enroll-role-${option.id}`}
                  data-selected={role === option.id ? "true" : "false"}
                  onClick={() => handleRoleSelect(option.id)}
                  style={{
                    borderRadius: 999,
                    border: `1px solid ${role === option.id ? C.seafoam : "#1e3448"}`,
                    background: role === option.id ? "rgba(46,191,165,0.12)" : "transparent",
                    color: C.white,
                    padding: "10px 14px",
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {option.label}
                  {option.beta && (
                    <span
                      style={{
                        fontSize: 8,
                        fontWeight: 800,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        color: C.warning,
                        border: `1px solid ${C.warning}`,
                        borderRadius: 999,
                        padding: "1px 5px",
                        lineHeight: 1.2,
                      }}
                    >
                      beta
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 10 }}>
              {ROLE_OPTIONS.find((option) => option.id === role)?.help}
            </div>
          </div>

          {/* Book-buyer offer — Anglers only. Presented after the role is chosen. */}
          {role === "fisherman" && (
          <div
            style={{
              border: `1px solid ${bookVerified ? C.seafoam : C.border}`,
              borderRadius: 10,
              background: C.cardBg,
              padding: 10,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 11, color: C.white, fontWeight: 700 }}>
              Bought <em>Before the Cast</em>?
            </div>
            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45 }}>
              Scan the QR code printed inside your book to unlock your <strong>6-month reader reward</strong>. It'll be applied to your account automatically.
            </div>

            {bookVerified ? (
              <div
                data-testid="enroll-book-verified"
                style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 10, color: C.seafoam }}
              >
                <span style={{ marginTop: 1 }}>✓</span>
                <span>{bookScanMessage || "Verified — reader reward unlocked."}</span>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  data-testid="enroll-book-scan"
                  onClick={() => { setBookScanState("scanning"); setShowBookScanner(true); }}
                  disabled={bookScanState === "verifying"}
                  style={{
                    alignSelf: "flex-start",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "9px 16px",
                    borderRadius: 999,
                    border: `1px solid ${C.seafoam}`,
                    background: "rgba(6,10,16,0.55)",
                    color: C.white,
                    fontSize: 11,
                    cursor: bookScanState === "verifying" ? "default" : "pointer",
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M3 3h6v2H5v4H3V3zm12 0h6v6h-2V5h-4V3zM3 15h2v4h4v2H3v-6zm16 0h2v6h-6v-2h4v-4zM7 7h4v4H7V7zm0 6h2v2H7v-2zm4 4h2v-2h-2v2zm2-10h4v2h-2v2h-2V7zm0 6h2v2h-2v-2zm4 0h2v4h-4v-2h2v-2z" fill={C.seafoam}/>
                  </svg>
                  {bookScanState === "verifying" ? "Verifying…" : "Scan book QR code"}
                </button>
                {bookScanState === "error" && bookScanMessage && (
                  <div style={{ fontSize: 10, color: C.warning, lineHeight: 1.4 }}>{bookScanMessage}</div>
                )}
              </>
            )}
          </div>
          )}

          {pendingRole && ROLE_CONFIRM[pendingRole] && (
            <div
              role="dialog"
              aria-modal="true"
              data-testid="enroll-role-confirm-modal"
              onClick={cancelPendingRole}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 1000,
                background: "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
              }}
            >
              <div
                onClick={(event) => event.stopPropagation()}
                style={{
                  width: "100%",
                  maxWidth: 380,
                  background: C.cardBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  padding: 20,
                  maxHeight: "85vh",
                  overflowY: "auto",
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 700, color: C.white, marginBottom: 12 }}>
                  {ROLE_CONFIRM[pendingRole].title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: C.muted,
                    lineHeight: 1.6,
                    whiteSpace: "pre-line",
                    marginBottom: 18,
                  }}
                >
                  {ROLE_CONFIRM[pendingRole].body}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button
                    type="button"
                    onClick={confirmPendingRole}
                    variant="primary"
                    data-testid={`enroll-role-confirm-${pendingRole}`}
                    style={{ flex: 1 }}
                  >
                    {ROLE_CONFIRM[pendingRole].confirmLabel}
                  </Button>
                  <Button
                    type="button"
                    onClick={cancelPendingRole}
                    variant="ghost"
                    data-testid="enroll-role-cancel"
                    style={{ flex: 1 }}
                  >
                    Go back
                  </Button>
                </div>
              </div>
            </div>
          )}

          {role === "fisherman" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>WHERE DO YOU FISH?</div>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, background: C.cardBg }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {WATER_TYPE_OPTIONS.map((option) => {
                    const selected = waterTypes.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        id={`enroll-watertype-${option.id}`}
                        data-testid={`enroll-watertype-${option.id}`}
                        data-selected={selected ? "true" : "false"}
                        onClick={() => {
                          setWaterTypes((prev) =>
                            prev.includes(option.id)
                              ? prev.filter((id) => id !== option.id)
                              : [...prev, option.id]
                          );
                          // Drop any subtypes that belong to a now-deselected
                          // water type so we never save orphaned subtypes.
                          if (waterTypes.includes(option.id)) {
                            const removedIds = (SUBTYPE_OPTIONS[option.id] || []).map((o) => o.id);
                            setSubTypes((prev) => prev.filter((id) => !removedIds.includes(id)));
                          }
                        }}
                        style={{
                          borderRadius: 999,
                          border: `1px solid ${selected ? C.seafoam : "#1e3448"}`,
                          background: selected ? "rgba(46,191,165,0.12)" : "transparent",
                          color: C.white,
                          padding: "10px 14px",
                          cursor: "pointer",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                {waterTypes.map((wt) => (
                  <div key={wt} style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                    <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>
                      {wt === "saltwater"
                        ? "SALTWATER — INSHORE, OFFSHORE, OR BOTH?"
                        : "FRESHWATER — RIVER, LAKE, OR BOTH?"}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {(SUBTYPE_OPTIONS[wt] || []).map((option) => {
                        const selected = subTypes.includes(option.id);
                        return (
                          <button
                            key={option.id}
                            type="button"
                            id={`enroll-subtype-${option.id}`}
                            data-testid={`enroll-subtype-${option.id}`}
                            data-selected={selected ? "true" : "false"}
                            onClick={() =>
                              setSubTypes((prev) =>
                                prev.includes(option.id)
                                  ? prev.filter((id) => id !== option.id)
                                  : [...prev, option.id]
                              )
                            }
                            style={{
                              borderRadius: 999,
                              border: `1px solid ${selected ? C.seafoam : "#1e3448"}`,
                              background: selected ? "rgba(46,191,165,0.12)" : "transparent",
                              color: C.white,
                              padding: "10px 14px",
                              cursor: "pointer",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    {(SUBTYPE_OPTIONS[wt] || []).some((opt) => subTypes.includes(opt.id)) && (
                      <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.5 }}>
                        {(SUBTYPE_OPTIONS[wt] || [])
                          .filter((opt) => subTypes.includes(opt.id))
                          .map((opt) => `${opt.label}: ${opt.help}`)
                          .join(" ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <TrialTrackPickers
                waterType={waterTypes.length === 1 ? waterTypes[0] : ""}
                trialSpecies={trialSpecies}
                setTrialSpecies={setTrialSpecies}
                trialLure={trialLure}
                setTrialLure={setTrialLure}
                trialLiveBait={trialLiveBait}
                setTrialLiveBait={setTrialLiveBait}
                speciesQuery={speciesQuery}
                setSpeciesQuery={setSpeciesQuery}
                lureQuery={lureQuery}
                setLureQuery={setLureQuery}
                liveBaitQuery={liveBaitQuery}
                setLiveBaitQuery={setLiveBaitQuery}
                showSpeciesList={showSpeciesList}
                setShowSpeciesList={setShowSpeciesList}
                showLureList={showLureList}
                setShowLureList={setShowLureList}
                showLiveBaitList={showLiveBaitList}
                setShowLiveBaitList={setShowLiveBaitList}
              />
              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  background: C.cardBg,
                  padding: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div style={{ fontSize: 11, color: C.white, fontWeight: 700 }}>Video Sharing</div>
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 10, color: C.white, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    id="enroll-share-videos"
                    data-testid="enroll-share-videos"
                    checked={shareVideos}
                    onChange={(e) => setShareVideos(e.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span>Share my catch videos on the dashboard, and let me post individual certified clips publicly to THE 99's YouTube channel. Each YouTube post is optional — you'll be asked to confirm after a clip is certified. You can turn this off anytime in User Preferences.</span>
                </label>
              </div>
            </>
          )}
          </div>

          {error && <div style={{ fontSize: 11, color: C.warning }}>{error}</div>}

          <Button
            type="submit"
            variant="primary"
            fullWidth
            id="enroll-submit"
            data-testid="enroll-submit"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Enrollment"}
          </Button>

          <Button
            variant="ghost"
            fullWidth
            disabled={loading}
            onClick={() => onCancel && onCancel()}
          >
            Cancel Enrollment
          </Button>
        </form>
      </Card>
    </div>
  );
}

function TrialTrackPickers({
  waterType,
  trialSpecies,
  setTrialSpecies,
  trialLure,
  setTrialLure,
  trialLiveBait,
  setTrialLiveBait,
  speciesQuery,
  setSpeciesQuery,
  lureQuery,
  setLureQuery,
  liveBaitQuery,
  setLiveBaitQuery,
  showSpeciesList,
  setShowSpeciesList,
  showLureList,
  setShowLureList,
  showLiveBaitList,
  setShowLiveBaitList,
}) {
  const { species, lures, loading, error } = useAnglerCatalog();

  const speciesResults = React.useMemo(
    () => searchSpecies(species, speciesQuery, waterType),
    [species, speciesQuery, waterType],
  );
  const lureResults = React.useMemo(
    () => searchLures(lures, lureQuery, "lure"),
    [lures, lureQuery],
  );
  const liveBaitResults = React.useMemo(
    () => searchLures(lures, liveBaitQuery, "liveBait"),
    [lures, liveBaitQuery],
  );

  const speciesLabel = trialSpecies
    ? findById(species, trialSpecies)?.label || trialSpecies
    : "";
  const lureLabel = trialLure
    ? findById(lures, trialLure)?.label || trialLure
    : "";
  const liveBaitLabel = trialLiveBait
    ? findById(lures, trialLiveBait)?.label || trialLiveBait
    : "";

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.cardBg,
    color: C.white,
    outline: "none",
    fontSize: 12,
  };

  const dropdownStyle = {
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    background: C.cardBg,
    marginTop: 4,
    maxHeight: 180,
    overflowY: "auto",
  };

  const itemStyle = {
    padding: "8px 12px",
    fontSize: 12,
    color: C.white,
    cursor: "pointer",
    borderBottom: `1px solid ${C.border}33`,
  };

  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        background: C.cardBg,
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>
        TRIAL TRACKING
      </div>
      <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45 }}>
        Pick the fish species you want to track, plus at least one lure or
        live bait. You'll add the rest later from the dashboard.
      </div>
      {loading && (
        <div style={{ fontSize: 10, color: C.muted }}>Loading catalog…</div>
      )}
      {error && (
        <div style={{ fontSize: 10, color: C.warning }}>
          Couldn't load the catalog. You can skip this and add picks later.
        </div>
      )}

      {/* Species */}
      <div>
        <div style={{ fontSize: 10, color: C.white, marginBottom: 4 }}>
          Fish species to track <span style={{ color: C.warning }}>*</span>
        </div>
        {trialSpecies ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: `1px solid ${C.seafoam}`,
              borderRadius: 10,
              padding: "8px 10px",
              background: "rgba(46,191,165,0.10)",
            }}
          >
            <span style={{ fontSize: 12, color: C.white }}>{speciesLabel}</span>
            <button
              type="button"
              onClick={() => {
                setTrialSpecies("");
                setSpeciesQuery("");
              }}
              style={{
                background: "none",
                border: "none",
                color: C.muted,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              id="enroll-trial-fish-input"
              data-testid="enroll-trial-fish-input"
              placeholder="Search species (e.g. Redfish)"
              value={speciesQuery}
              onFocus={() => setShowSpeciesList(true)}
              onClick={() => setShowSpeciesList(true)}
              onChange={(e) => {
                setSpeciesQuery(e.target.value);
                setShowSpeciesList(true);
              }}
              style={inputStyle}
            />
            {showSpeciesList && speciesResults.length > 0 && (
              <div
                data-testid="enroll-trial-fish-list"
                className="hide-scrollbar"
                style={dropdownStyle}
              >
                {speciesResults.map((entry, index) => (
                  <div
                    key={entry.id}
                    id={`enroll-trial-fish-list-item-${index}`}
                    data-testid={`enroll-trial-fish-list-item-${index}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setTrialSpecies(entry.id);
                      setSpeciesQuery("");
                      setShowSpeciesList(false);
                    }}
                    style={itemStyle}
                  >
                    {entry.label}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lure */}
      <div>
        <div style={{ fontSize: 10, color: C.white, marginBottom: 4 }}>
          One lure to track <span style={{ color: C.muted }}>(lure or live bait required)</span>
        </div>
        {trialLure ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: `1px solid ${C.seafoam}`,
              borderRadius: 10,
              padding: "8px 10px",
              background: "rgba(46,191,165,0.10)",
            }}
          >
            <span style={{ fontSize: 12, color: C.white }}>{lureLabel}</span>
            <button
              type="button"
              onClick={() => {
                setTrialLure("");
                setLureQuery("");
              }}
              style={{
                background: "none",
                border: "none",
                color: C.muted,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              id="enroll-trial-lure-input"
              data-testid="enroll-trial-lure-input"
              placeholder="Search lures (e.g. soft plastic)"
              value={lureQuery}
              onFocus={() => setShowLureList(true)}
              onClick={() => setShowLureList(true)}
              onChange={(e) => {
                setLureQuery(e.target.value);
                setShowLureList(true);
              }}
              style={inputStyle}
            />
            {showLureList && lureResults.length > 0 && (
              <div
                data-testid="enroll-trial-lure-list"
                className="hide-scrollbar"
                style={dropdownStyle}
              >
                {lureResults.map((entry, index) => (
                  <div
                    key={entry.id}
                    id={`enroll-trial-lure-list-item-${index}`}
                    data-testid={`enroll-trial-lure-list-item-${index}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setTrialLure(entry.id);
                      setLureQuery("");
                      setShowLureList(false);
                    }}
                    style={itemStyle}
                  >
                    {entry.label}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Live bait */}
      <div>
        <div style={{ fontSize: 10, color: C.white, marginBottom: 4 }}>
          One live bait to track <span style={{ color: C.muted }}>(lure or live bait required)</span>
        </div>
        {trialLiveBait ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: `1px solid ${C.seafoam}`,
              borderRadius: 10,
              padding: "8px 10px",
              background: "rgba(46,191,165,0.10)",
            }}
          >
            <span style={{ fontSize: 12, color: C.white }}>{liveBaitLabel}</span>
            <button
              type="button"
              onClick={() => {
                setTrialLiveBait("");
                setLiveBaitQuery("");
              }}
              style={{
                background: "none",
                border: "none",
                color: C.muted,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <input
              type="text"
              id="enroll-trial-livebait-input"
              data-testid="enroll-trial-livebait-input"
              placeholder="Search live bait (e.g. shrimp)"
              value={liveBaitQuery}
              onFocus={() => setShowLiveBaitList(true)}
              onClick={() => setShowLiveBaitList(true)}
              onChange={(e) => {
                setLiveBaitQuery(e.target.value);
                setShowLiveBaitList(true);
              }}
              style={inputStyle}
            />
            {showLiveBaitList && liveBaitResults.length > 0 && (
              <div
                data-testid="enroll-trial-livebait-list"
                className="hide-scrollbar"
                style={dropdownStyle}
              >
                {liveBaitResults.map((entry, index) => (
                  <div
                    key={entry.id}
                    id={`enroll-trial-livebait-list-item-${index}`}
                    data-testid={`enroll-trial-livebait-list-item-${index}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setTrialLiveBait(entry.id);
                      setLiveBaitQuery("");
                      setShowLiveBaitList(false);
                    }}
                    style={itemStyle}
                  >
                    {entry.label}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
